import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Layout } from "@/components/Layout";
import { WahlartCards } from "@/components/wahlkampfagentur/WahlartCards";
import { BausteineWahlerfolg } from "@/components/wahlen/BausteineWahlerfolg";
import { GruendeCreategy } from "@/components/wahlen/GruendeCreategy";
import { VoteBoxAnimation } from "@/components/buergermeisterwahl/VoteBoxAnimation";
import { CasesSection } from "@/components/cases/CasesSection";
import { TestimonialsSlider } from "@/components/home/TestimonialsSlider";
import {
  allPublicCasesQueryOptions,
  testimonialsQueryOptions,
  type TestimonialCategory,
} from "@/lib/public-content.queries";

/** Wahlkampf-Kategorien für Referenzen und Stimmen (keine Kommunikationskunden). */
const WAHLKAMPF_KATEGORIEN: TestimonialCategory[] = [
  "buergermeisterwahl",
  "direktkandidaten",
  "parteikampagnen",
  "personalratswahl",
];

const TITLE = "Wahlkampfagentur für Wahlkämpfe in Deutschland | creategy";
const DESCRIPTION =
  "Wahlkampfagentur, die nur Wahlkampf macht: über 200 Wahlkämpfe für Bürgermeister, Direktmandate, Parteien und Personalräte. 72,6 Prozent Siegquote.";
const URL = "https://creategy.de/wahlkampfagentur";

export const Route = createFileRoute("/wahlkampfagentur")({
  loader: async ({ context }) => {
    const preload = Promise.allSettled([
      context.queryClient.prefetchQuery(allPublicCasesQueryOptions()),
      context.queryClient.prefetchQuery(testimonialsQueryOptions),
    ]);

    // Daten möglichst schon ins SSR-HTML aufnehmen, die Route bei einem
    // langsamen Backend aber niemals unbegrenzt blockieren.
    await Promise.race([
      preload,
      new Promise<void>((resolve) => setTimeout(resolve, 4_000)),
    ]);
  },
  pendingMs: 300,
  pendingMinMs: 200,
  pendingComponent: WahlkampfagenturPending,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:site_name", content: "creategy" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: WahlkampfagenturPage,
});

function WahlkampfagenturPending() {
  return (
    <div className="min-h-dvh bg-background" aria-busy="true" aria-label="Seite wird geladen">
      <div className="section-y-hero mx-auto grid w-full max-w-[1400px] gap-12 px-6 md:grid-cols-2 md:px-12">
        <div className="space-y-6">
          <div className="h-14 w-full max-w-xl animate-pulse rounded-md bg-secondary" />
          <div className="h-24 w-full max-w-2xl animate-pulse rounded-md bg-secondary/70" />
        </div>
        <div className="mx-auto aspect-square w-full max-w-[360px] animate-pulse rounded-md bg-secondary/70 md:ml-auto md:mr-0" />
      </div>
    </div>
  );
}

function WahlkampfagenturPage() {
  const testimonials = useQuery(testimonialsQueryOptions);
  // Solange keine Daten geladen sind, KEINE leere Liste durchreichen –
  // sonst zeigt der Slider den Leerzustand statt des Ladezustands.
  const wahlkampfStimmen = testimonials.data
    ? testimonials.data.filter((t) =>
        (t.categories ?? []).some((c) => WAHLKAMPF_KATEGORIEN.includes(c)),
      )
    : undefined;

  return (
    <Layout>
      {/* 1. Kopfbereich – Höhe und Zweiteilung wie auf den Wahlkampf-Produktseiten */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div>
              <h1 id="hero" className="heading-h1 text-brand-wahlen">
                Die Wahlkampf<span className="md:hidden">{'\u00AD'}</span>agentur mit 72,6 Prozent Erfolgsquote.
              </h1>
              <p className="mt-8 max-w-3xl font-body text-base leading-relaxed text-foreground md:text-lg">
                Als Spezialagentur für Wahlkampf haben wir seit 2021 bereits über 200
                Kandidatinnen und Kandidaten sowie Parteien durch ihre Wahlkämpfe
                begleitet. Wir verstehen, was einen erfolgreichen Wahlkampf ausmacht.
              </p>

            </div>

            {/* Vote-Box Scroll-Animation – wie auf der Bürgermeisterwahl-Seite */}
            <div className="order-first mx-auto w-full max-w-[240px] sm:max-w-[280px] md:order-none md:ml-auto md:mr-0 md:max-w-[360px] lg:max-w-[420px]">
              <VoteBoxAnimation />
            </div>
          </div>
        </div>
      </section>


      {/* 2. Gabelung – unmittelbar unter dem Kopfbereich, ohne Zwischenabschnitt */}
      <section aria-labelledby="gabelung" id="gabelung" className="scroll-mt-24">
        <div className="mx-auto w-full max-w-[1400px] px-6 pb-16 md:px-12 md:pb-20">
          <h2 id="gabelung-titel" className="heading-h2 text-primary">
            Für welche Wahl treten Sie an?
          </h2>
          <p className="mt-4 max-w-3xl font-body text-sm leading-relaxed text-foreground/80 md:text-base">
            Wählen Sie Ihre Wahlart. Sie landen direkt bei den Leistungen, die dafür zählen.
          </p>

          <div className="mt-10">
            <WahlartCards />
          </div>
        </div>
      </section>

      {/* 3. Belege – Projekte & Referenzen der Wahlkampf-Produktseiten */}
      <CasesSection
        includeTags={["buergermeisterwahl", "direktkandidaten", "parteikampagnen", "personalratswahl"]}
        headingColor="text-brand-wahlen"
        description="Ausgewählte Wahlkämpfe aus über zweihundert Kampagnen."
        showCta
      />

      {/* 4. Die Bausteine Ihres Wahlerfolgs */}
      <BausteineWahlerfolg sectionClassName="border-t border-border" />

      {/* 5. Fünf Gründe für creategy */}
      <GruendeCreategy
        sectionClassName="border-t border-border"
        description="Warum Kandidatinnen, Kandidaten und Parteien sich für creategy entscheiden: fünf Gründe, die im Wahlkampf den Unterschied machen."
      />

      <TestimonialsSlider
        testimonials={wahlkampfStimmen}
        showFilters={false}
        headingColor="text-brand-wahlen"
        title={
          <>
            Stimmen aus dem{" "}
            <span className="font-display font-extrabold">Wahlkampf.</span>
          </>
        }
      />

    </Layout>
  );
}
